/**
 * Sanitizador de dinero — la regla número 1 de SymTrack.
 *
 * Luis lo pidió dos veces, con estas palabras:
 *   "No me interesa nada de lo de la plata: cuánto vale y cuánto se va a ganar.
 *    Eso no es la finalidad para lo que se quiere del sistema."
 *
 * Consecuencia buscada: la base de datos de SymTrack NO contiene un solo peso.
 * Así el portal del cliente no puede filtrar un valor de compra ni por un error
 * de programación, porque el dato sencillamente no está.
 *
 * Todo lo que entra de la API pasa por aquí ANTES de tocar la base de datos,
 * incluido el raw_json que se guarda por trazabilidad.
 *
 * Ver PLAN.md §0 · CLAUDE.md regla 1 · campos-dinero.json
 */

import config from './campos-dinero.json' with { type: 'json' }

const EXACTOS: ReadonlySet<string> = new Set(config.exactos)
const SOSPECHOSOS: readonly string[] = config.sospechosos.map((s) => s.toLowerCase())
const EXCEPCIONES: ReadonlySet<string> = new Set(config.excepciones)

/** Por qué se descartó un campo. */
export type MotivoDescarte = 'exacto' | 'sospechoso'

export interface CampoDescartado {
  /** Ruta completa dentro del objeto, ej. `remesas[0].valorCobrar` */
  readonly ruta: string
  readonly campo: string
  readonly motivo: MotivoDescarte
}

export interface ResultadoSanitizacion<T> {
  /** El objeto sin un solo campo de dinero. */
  readonly limpio: T
  /**
   * Lo que se quitó. Los de motivo `sospechoso` son los que importan:
   * significan que Vulcano mandó un campo monetario que nadie documentó,
   * y alguien tiene que mirarlo.
   */
  readonly descartados: readonly CampoDescartado[]
}

/**
 * Decide si un campo es dinero.
 *
 * Orden deliberado: las excepciones ganan sobre la regla defensiva, porque
 * `pesoTotal` contiene "total" y no es plata. Pero NO ganan sobre la lista
 * exacta — si algo está en `exactos`, se va, sin discusión.
 */
export function clasificarCampo(campo: string): MotivoDescarte | null {
  if (EXACTOS.has(campo)) return 'exacto'
  if (EXCEPCIONES.has(campo)) return null

  const enMinusculas = campo.toLowerCase()
  for (const fragmento of SOSPECHOSOS) {
    if (enMinusculas.includes(fragmento)) return 'sospechoso'
  }
  return null
}

/**
 * Quita todo rastro de dinero de un objeto, recursivamente.
 *
 * Recorre arrays y objetos anidados, así que cubre el caso real de Vulcano:
 * `pedido.remesas[].costos[]`, donde el dinero vive tres niveles abajo.
 *
 * No muta la entrada: devuelve una copia limpia.
 */
export function sanitizarDinero<T>(valor: T): ResultadoSanitizacion<T> {
  const descartados: CampoDescartado[] = []
  const limpio = recorrer(valor, '', descartados) as T
  return { limpio, descartados }
}

function recorrer(valor: unknown, ruta: string, descartados: CampoDescartado[]): unknown {
  if (valor === null || typeof valor !== 'object') return valor

  if (Array.isArray(valor)) {
    return valor.map((item, i) => recorrer(item, `${ruta}[${i}]`, descartados))
  }

  const salida: Record<string, unknown> = {}
  for (const [campo, contenido] of Object.entries(valor as Record<string, unknown>)) {
    const rutaHija = ruta ? `${ruta}.${campo}` : campo
    const motivo = clasificarCampo(campo)

    if (motivo !== null) {
      descartados.push({ ruta: rutaHija, campo, motivo })
      continue
    }

    salida[campo] = recorrer(contenido, rutaHija, descartados)
  }
  return salida
}

/**
 * Los descartes que un humano tiene que revisar.
 *
 * Los `exacto` son rutina y no hace falta reportarlos: ya sabemos que llegan.
 * Los `sospechoso` son la alarma — un campo monetario nuevo y no documentado.
 */
export function descartesParaRevisar(
  descartados: readonly CampoDescartado[],
): readonly CampoDescartado[] {
  return descartados.filter((d) => d.motivo === 'sospechoso')
}

/**
 * Verificación de último recurso, para usar en pruebas y en el sync.
 * Si esto devuelve algo, hay un bug: se coló plata.
 */
export function auditarSinDinero(valor: unknown): readonly string[] {
  const encontrados: CampoDescartado[] = []
  recorrerSoloAuditando(valor, '', encontrados)
  return encontrados.map((d) => d.ruta)
}

function recorrerSoloAuditando(valor: unknown, ruta: string, hallados: CampoDescartado[]): void {
  if (valor === null || typeof valor !== 'object') return

  if (Array.isArray(valor)) {
    valor.forEach((item, i) => recorrerSoloAuditando(item, `${ruta}[${i}]`, hallados))
    return
  }

  for (const [campo, contenido] of Object.entries(valor as Record<string, unknown>)) {
    const rutaHija = ruta ? `${ruta}.${campo}` : campo
    const motivo = clasificarCampo(campo)
    if (motivo !== null) hallados.push({ ruta: rutaHija, campo, motivo })
    recorrerSoloAuditando(contenido, rutaHija, hallados)
  }
}
